import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkle, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import { sendMessage, clearChat } from "@/state/chat/chat.slice";
import MarkdownMessage from "./MarkdownMessage";

const SUGGESTIONS = [
    "Summarize today's sales",
    "What's low on stock?",
    "This month's sales report",
    "Top 5 products last 7 days",
];

const TypingDots = () => (
    <div className="flex items-center gap-1 px-1 py-2">
        {[0, 1, 2].map((i) => (
            <span key={i} className="h-2 w-2 rounded-full bg-muted-foreground/60 animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }} />
        ))}
    </div>
);

export default function ChatPanel() {
    const dispatch = useDispatch();
    const { messages, isLoading } = useSelector((state) => state.chat);
    const [input, setInput] = useState("");
    const scrollRef = useRef(null);

    useEffect(() => {
        // Auto-scroll to the newest message.
        const el = scrollRef.current?.querySelector("[data-scroll-anchor]");
        el?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, isLoading]);

    const submit = (text) => {
        const value = (text ?? input).trim();
        if (!value || isLoading) return;
        dispatch(sendMessage(value));
        setInput("");
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Messages */}
            <ScrollArea className="flex-1 px-4" ref={scrollRef}>
                <div className="py-4 space-y-4">
                    {messages.length === 0 && (
                        <div className="text-center text-muted-foreground mt-8 space-y-3">
                            <Sparkle className="h-10 w-10 mx-auto text-primary" weight="duotone" />
                            <p className="text-sm font-medium">Ask about your sales, stock, or reports.</p>
                            <div className="flex flex-wrap gap-2 justify-center pt-2">
                                {SUGGESTIONS.map((s) => (
                                    <button key={s} onClick={() => submit(s)}
                                        className="text-xs px-3 py-1.5 rounded-full border hover:bg-muted transition-colors">
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((m) => (
                        <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                            {m.role === "user" ? (
                                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary text-primary-foreground px-3.5 py-2 text-sm whitespace-pre-wrap">
                                    {m.content}
                                </div>
                            ) : (
                                <div className="max-w-[92%] w-full rounded-2xl rounded-bl-sm bg-muted/60 border px-3 py-3">
                                    {/* Before any text streams in, show the live status (e.g. "Fetching sales summary…"). */}
                                    {!m.text && m.streaming ? (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <TypingDots />
                                            {m.status && <span>{m.status}</span>}
                                        </div>
                                    ) : (
                                        <MarkdownMessage text={m.text} streaming={m.streaming} />
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    <div data-scroll-anchor />
                </div>
            </ScrollArea>

            {/* Composer */}
            <div className="border-t p-3 space-y-2">
                <div className="flex items-end gap-2">
                    <Textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Ask about sales, stock, reports…"
                        rows={1}
                        className="resize-none min-h-[40px] max-h-32 text-sm"
                        disabled={isLoading}
                    />
                    <Button size="icon" onClick={() => submit()} disabled={isLoading || !input.trim()} className="shrink-0">
                        <PaperPlaneTilt className="h-4 w-4" weight="fill" />
                    </Button>
                </div>
                <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground">AI can be inaccurate — verify important figures.</p>
                    {messages.length > 0 && (
                        <button onClick={() => dispatch(clearChat())}
                            className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                            <Trash className="h-3 w-3" /> Clear
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
